// BILLING-001 Adyvance Wallet client (owner, 2026-10-25; D-064): the wallet page, top-ups by receipt,
// plans and auto-renew, root AI credit packs, premium themes and add-ons, all paid from the wallet.
// Shapes: @ady/contracts wallet-service. After any purchase, `refreshWallet` refetches every screen
// that shows a balance (wallet, top bar, SMS, ads, Import Marketplace, plan and billing).
import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  WALLET_SHORT_CODE,
  type AddonOfferView,
  type AdyGateway,
  type AdyPayIntent,
  type AdyPayment,
  type AdyPayStart,
  type AiCreditAccount,
  type CheckoutQuote,
  type InvoiceView,
  type ShopReferral,
  type PlanPeriod,
  type PremiumTheme,
  type WalletActivity,
  type WalletOverview,
  type WalletPlans,
  type WalletShortDetails,
  type WalletTopup,
  type WalletTopupMethod,
} from "@ady/contracts";
import { ApiError, request } from "../api/client";

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

export interface TopupBody {
  amount_paisa: number;
  method: WalletTopupMethod;
  reference?: string;
  proof?: string;
  note?: string;
}

export const adyWalletApi = {
  overview: (orgId: string) => request<WalletOverview>("GET", `${org(orgId)}/wallet/overview`),
  activity: (orgId: string, q: { before?: string; direction?: "in" | "out" } = {}) =>
    request<{ data: WalletActivity[]; next_before: string | null }>(
      "GET",
      `${org(orgId)}/wallet/activity`,
      { query: { ...q, limit: 30 } },
    ),
  topup: (orgId: string, body: TopupBody, idempotencyKey: string) =>
    request<WalletTopup>("POST", `${org(orgId)}/wallet/topups`, { body, idempotencyKey }),
  cancelTopup: (orgId: string, topupId: string) =>
    request<{ ok: true }>(
      "POST",
      `${org(orgId)}/wallet/topups/${encodeURIComponent(topupId)}/cancel`,
    ),
  plans: (orgId: string) => request<WalletPlans>("GET", `${org(orgId)}/billing/plans`),
  checkout: (
    orgId: string,
    body: { plan_id: string; period: PlanPeriod; auto_renew: boolean; coupon_code?: string },
    idempotencyKey: string,
  ) =>
    request<{
      ends_at: string;
      charged_paisa: number;
      credited_paisa: number;
      discount_paisa?: number;
      renewed: boolean;
    }>("POST", `${org(orgId)}/billing/checkout`, { body, idempotencyKey }),
  /** D-067: the price of a plan period with a coupon (or the coupon a renewal still has). */
  quote: (orgId: string, body: { plan_id: string; period: PlanPeriod; coupon_code?: string }) =>
    request<CheckoutQuote>("POST", `${org(orgId)}/billing/quote`, { body }),
  invoice: (orgId: string, invoiceId: string) =>
    request<InvoiceView>("GET", `${org(orgId)}/billing/invoices/${encodeURIComponent(invoiceId)}`),
  referral: (orgId: string) => request<ShopReferral>("GET", `${org(orgId)}/referrals`),
  autoRenew: (orgId: string, on: boolean) =>
    request<WalletPlans>("PUT", `${org(orgId)}/billing/auto-renew`, { body: { on } }),
  addons: (orgId: string) =>
    request<{ offers: (AddonOfferView & { purchasable: boolean })[] }>(
      "GET",
      `${org(orgId)}/billing/addons`,
    ),
  buyAddon: (
    orgId: string,
    body: { offer_key: string; quantity: number },
    idempotencyKey: string,
  ) =>
    request<{ id: string; ends_at: string }>("POST", `${org(orgId)}/billing/addons`, {
      body,
      idempotencyKey,
    }),
  ai: (orgId: string) => request<AiCreditAccount>("GET", `${org(orgId)}/ai-credits`),
  buyAi: (orgId: string, packId: string, idempotencyKey: string) =>
    request<AiCreditAccount>("POST", `${org(orgId)}/ai-credits/purchases`, {
      body: { pack_id: packId },
      idempotencyKey,
    }),
  themes: (orgId: string) =>
    request<{ data: PremiumTheme[] }>("GET", `${org(orgId)}/themes/premium`),
  buyTheme: (orgId: string, key: string, idempotencyKey: string) =>
    request<PremiumTheme>("POST", `${org(orgId)}/themes/premium/${encodeURIComponent(key)}`, {
      idempotencyKey,
    }),
  /** D-085: pay Adyvance by Fonepay Dynamic QR (a top-up, or a direct purchase priced by the server). */
  startOnline: (
    orgId: string,
    body: {
      provider: AdyGateway;
      intent: AdyPayIntent;
      amount_paisa?: number;
    },
    idempotencyKey: string,
  ) =>
    request<AdyPayStart>("POST", `${org(orgId)}/wallet/online-payments`, { body, idempotencyKey }),
  onlinePayment: (orgId: string, paymentId: string) =>
    request<AdyPayment>(
      "GET",
      `${org(orgId)}/wallet/online-payments/${encodeURIComponent(paymentId)}`,
    ),
};

export const adyWalletKeys = {
  all: (orgId: string) => ["org", orgId, "adyWallet"] as const,
  overview: (orgId: string) => [...adyWalletKeys.all(orgId), "overview"] as const,
  activity: (orgId: string, direction: string) =>
    [...adyWalletKeys.all(orgId), "activity", direction] as const,
  plans: (orgId: string) => [...adyWalletKeys.all(orgId), "plans"] as const,
  addons: (orgId: string) => [...adyWalletKeys.all(orgId), "addons"] as const,
  ai: (orgId: string) => [...adyWalletKeys.all(orgId), "ai"] as const,
  themes: (orgId: string) => [...adyWalletKeys.all(orgId), "themes"] as const,
};

export const useWalletOverview = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: adyWalletKeys.overview(orgId),
    queryFn: () => adyWalletApi.overview(orgId),
    enabled,
    staleTime: 15_000,
  });
export const useWalletPlans = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: adyWalletKeys.plans(orgId),
    queryFn: () => adyWalletApi.plans(orgId),
    enabled,
  });
export const useAiCredits = (orgId: string, enabled = true) =>
  useQuery({ queryKey: adyWalletKeys.ai(orgId), queryFn: () => adyWalletApi.ai(orgId), enabled });
export const usePremiumThemes = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: adyWalletKeys.themes(orgId),
    queryFn: () => adyWalletApi.themes(orgId),
    enabled,
  });
export const useAddonOffers = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: adyWalletKeys.addons(orgId),
    queryFn: () => adyWalletApi.addons(orgId),
    enabled,
  });

/** Every query that shows wallet money or what it bought, after a top-up or a purchase. */
export async function refreshWallet(qc: QueryClient, orgId: string): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: adyWalletKeys.all(orgId) }),
    qc.invalidateQueries({ queryKey: ["org", orgId, "sms"] }),
    qc.invalidateQueries({ queryKey: ["org", orgId, "ads"] }),
    qc.invalidateQueries({ queryKey: ["org", orgId, "import"] }),
    qc.invalidateQueries({ queryKey: ["wallet", orgId] }),
    qc.invalidateQueries({ queryKey: ["org", orgId, "billing"] }),
    qc.invalidateQueries({ queryKey: ["org", orgId, "entitlements"] }),
  ]);
}

/** The shortfall from a `billing.insufficient_balance` answer, else null. */
export function walletShortOf(err: unknown): WalletShortDetails | null {
  if (!(err instanceof ApiError) || err.code !== WALLET_SHORT_CODE) return null;
  const d = err.details as Partial<WalletShortDetails> | undefined;
  if (typeof d?.short_paisa !== "number") return null;
  return {
    needed_paisa: d.needed_paisa ?? 0,
    available_paisa: d.available_paisa ?? 0,
    short_paisa: d.short_paisa,
  };
}

/** A top-up amount that covers `short`: rounded up to Rs 100 and never below the shop's minimum. */
export function topupFor(shortPaisa: number, minPaisa: number): number {
  return Math.max(minPaisa, Math.ceil(shortPaisa / 10_000) * 10_000);
}
